import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Paper,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Grid,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import { Add as AddIcon, Delete as DeleteIcon, People as PeopleIcon, NavigateNext as NavigateNextIcon, Edit as EditIcon, QrCode as QrCodeIcon } from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import MultiSelect from '../../../components/MultiSelect';
import AddAccountModal from './AddAccountModal';
import OtpQrModal from './OtpQrModal';
import { hasPermission } from '../../../utils/permissions';
import { maskPhoneDisplay } from '../../../utils/validation';
import {
    fetchInit,
    fetchAccounts,
    fetchAccountById,
    fetchRoleById,
    clearSelectedRoleDetail,
    addAccount,
    updateAccount,
    selectAccounts,
    selectRoles,
    selectUnits,
    selectPositions,
    selectModules,
    selectSelectedRoleDetail,
    selectAccountsLoading,
    selectAccountsError,
    selectAccountsSuccessMessage,
    selectAccountsTotal,
    selectAccountsPage,
    selectAccountsPageSize,
    selectAccountsSortColumn,
    selectAccountsSortDirection,
    selectAccountsFilterUsername,
    selectAccountsFilterRoles,
    selectAccountsFilterUnits,
    selectSelectedAccount,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterUsername,
    setFilterRoles,
    setFilterUnits,
    deleteAccount,
    bulkDeleteAccounts,
    clearError,
    clearSuccessMessage,
    requestOtpLogin,
    selectOtpLoginData,
    selectOtpLoginLoading,
    selectOtpLoginError,
    clearOtpLoginData,
} from './slice';
import './Accounts.css';

function Accounts() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const accounts = useSelector(selectAccounts);
    const roles = useSelector(selectRoles);
    const units = useSelector(selectUnits);
    const positions = useSelector(selectPositions);
    const modules = useSelector(selectModules);

    // Debug: Log positions from state
    useEffect(() => {
        console.log('Accounts page - positions from state:', positions);
        console.log('Accounts page - positions length:', positions?.length);
    }, [positions]);
    const selectedRoleDetail = useSelector(selectSelectedRoleDetail);
    const loading = useSelector(selectAccountsLoading);
    const error = useSelector(selectAccountsError);
    const successMessage = useSelector(selectAccountsSuccessMessage);
    const total = useSelector(selectAccountsTotal);
    const page = useSelector(selectAccountsPage);
    const pageSize = useSelector(selectAccountsPageSize);
    const sortColumn = useSelector(selectAccountsSortColumn);
    const sortDirection = useSelector(selectAccountsSortDirection);
    const filterUsername = useSelector(selectAccountsFilterUsername);
    const filterRoles = useSelector(selectAccountsFilterRoles);
    const filterUnits = useSelector(selectAccountsFilterUnits);
    const selectedAccount = useSelector(selectSelectedAccount);
    const otpLoginData = useSelector(selectOtpLoginData);
    const otpLoginLoading = useSelector(selectOtpLoginLoading);
    const otpLoginError = useSelector(selectOtpLoginError);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpUserAccount, setOtpUserAccount] = useState(null);

    // Check permissions
    const canAddUser = hasPermission('user', 'addUser');
    const canEditUser = hasPermission('user', 'editUser');
    const canDeleteUser = hasPermission('user', 'deleteUser');

    // Fetch init data on mount
    useEffect(() => {
        dispatch(fetchInit());
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchAccounts({ page, pageSize, sortColumn, sortDirection, filterUsername, filterRoles, filterUnits }));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterUsername, filterRoles, filterUnits]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification and close modals
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            // Close modals on success
            setShowAddDialog(false);
            setShowEditDialog(false);
            // Refetch accounts to update the list
            dispatch(fetchAccounts({ page, pageSize, sortColumn, sortDirection, filterUsername, filterRoles, filterUnits }));
        }
    }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterUsername, filterRoles, filterUnits]);

    // Show OTP error notification
    useEffect(() => {
        if (otpLoginError) {
            showNotification(otpLoginError, 'error');
        }
    }, [otpLoginError, showNotification]);

    // Define table columns
    const columns = [
        {
            key: 'username',
            label: t('accounts.table.username'),
        },
        {
            key: 'email',
            label: t('accounts.table.email'),
        },
        {
            key: 'phone',
            label: t('accounts.table.phone'),
            render: (value) => maskPhoneDisplay(value),
        },
        {
            key: 'role',
            label: t('accounts.table.role'),
        },
        {
            key: 'unitName',
            label: t('accounts.table.unit'),
        },
        {
            key: 'positionName',
            label: t('accounts.table.position'),
            render: (value, row) => {
                if (!value) return '-';
                const code = row.positionCode ? ` (${row.positionCode})` : '';
                return `${value}${code}`;
            },
        },
        {
            key: 'status',
            label: t('accounts.table.status'),
            render: (value) => {
                // value is the status field itself (true/false/null)
                // Handle both boolean and numeric status values
                const isActive = value === true || value === 1 || value === '1';
                const hasStatus = value !== undefined && value !== null;

                return (
                    <Box
                        component="span"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: hasStatus ? (isActive ? '#d4edda' : '#f8d7da') : '#e2e8f0',
                            color: hasStatus ? (isActive ? '#155724' : '#721c24') : '#64748b',
                        }}
                    >
                        {hasStatus ? (isActive ? t('accounts.status.active') : t('accounts.status.inactive')) : '-'}
                    </Box>
                );
            },
        },
    ];

    // Handle edit action
    const handleEdit = (row) => {
        // Fetch account details by ID
        dispatch(fetchAccountById(row.id));
        setShowEditDialog(true);
    };

    // Handle delete action - show confirmation dialog
    const handleDelete = (row) => {
        setAccountToDelete(row);
        setShowDeleteDialog(true);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (accountToDelete?.id) {
            dispatch(deleteAccount(accountToDelete.id));
        }
        setShowDeleteDialog(false);
        setAccountToDelete(null);
    };

    // Cancel delete action
    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setAccountToDelete(null);
    };

    // Handle bulk delete - show confirmation dialog
    const handleBulkDelete = () => {
        setShowBulkDeleteDialog(true);
    };

    // Confirm bulk delete action
    const handleConfirmBulkDelete = () => {
        dispatch(bulkDeleteAccounts(selectedIds));
        setShowBulkDeleteDialog(false);
        setSelectedIds([]);
    };

    // Cancel bulk delete action
    const handleCancelBulkDelete = () => {
        setShowBulkDeleteDialog(false);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    // Handle sort change
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
        // fetchAccounts will be called automatically by useEffect
    };

    // Handle filter change
    const handleFilterChange = (event) => {
        dispatch(setFilterUsername(event.target.value));
    };

    // Handle role filter change
    const handleRoleFilterChange = (event) => {
        dispatch(setFilterRoles(event.target.value));
    };

    // Handle unit filter change
    const handleUnitFilterChange = (event) => {
        dispatch(setFilterUnits(event.target.value));
    };

    // Handle add account - show dialog
    const handleAddAccount = () => {
        setShowAddDialog(true);
    };

    // Handle close add account dialog
    const handleCloseAddAccount = () => {
        setShowAddDialog(false);
    };

    // Confirm add account
    const handleConfirmAddAccount = (accountData) => {
        dispatch(addAccount(accountData));
        // Don't close here - modal will close only on success via useEffect
    };

    // Handle close edit account dialog
    const handleCloseEditAccount = () => {
        setShowEditDialog(false);
    };

    // Confirm edit account
    const handleConfirmEditAccount = (accountData) => {
        dispatch(updateAccount(accountData));
        // Don't close here - modal will close only on success via useEffect
    };

    // Handle OTP Login request
    const handleRequestOtpLogin = (row) => {
        setOtpUserAccount(row);
        setShowOtpModal(true);
        dispatch(requestOtpLogin(row.id));
    };

    // Handle close OTP modal
    const handleCloseOtpModal = () => {
        setShowOtpModal(false);
        setOtpUserAccount(null);
        dispatch(clearOtpLoginData());
    };

    // Custom render actions for table
    const renderTableActions = (row) => {
        return (
            <>
                {canEditUser && (
                    <Tooltip title={t('table.actions.edit')}>
                        <IconButton
                            onClick={() => handleEdit(row)}
                            color="primary"
                            size="small"
                            id={`accounts-table-button-edit-${row.id}`}
                            name={`accounts-table-button-edit-${row.id}`}
                            data-testid={`accounts-table-button-edit-${row.id}`}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canDeleteUser && (
                    <Tooltip title={t('table.actions.delete')}>
                        <IconButton
                            onClick={() => handleDelete(row)}
                            color="error"
                            size="small"
                            id={`accounts-table-button-delete-${row.id}`}
                            name={`accounts-table-button-delete-${row.id}`}
                            data-testid={`accounts-table-button-delete-${row.id}`}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={t('accounts.otpLogin.button')}>
                    <IconButton
                        onClick={() => handleRequestOtpLogin(row)}
                        color="secondary"
                        size="small"
                        id={`accounts-table-button-otp-${row.id}`}
                        name={`accounts-table-button-otp-${row.id}`}
                        data-testid={`accounts-table-button-otp-${row.id}`}
                    >
                        <QrCodeIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PeopleIcon fontSize="small" />
                        <Typography variant="body2">{t('accounts.breadcrumb.users')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('accounts.breadcrumb.accounts')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('accounts.title')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {canAddUser && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleAddAccount}
                                id="addAccount"
                                name="addAccount"
                                data-testid="accounts-button-addAccount"
                            >
                                {t('accounts.addAccount')}
                            </Button>
                        )}
                        {canDeleteUser && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDelete}
                                disabled={selectedIds.length === 0}
                                id="deleteSelected"
                                name="deleteSelected"
                                data-testid="accounts-button-deleteSelected"
                            >
                                {t('accounts.deleteSelected')}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Filter Section */}
                <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Input
                                label={t('accounts.filterByUsername')}
                                value={filterUsername}
                                onChange={handleFilterChange}
                                placeholder={t('accounts.filterByUsernamePlaceholder')}
                                id="filterUsername"
                                name="filterUsername"
                                data-testid="accounts-input-filterUsername"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MultiSelect
                                label={t('accounts.filterByRole')}
                                value={filterRoles}
                                onChange={handleRoleFilterChange}
                                options={roles}
                                placeholder={t('accounts.filterByRolePlaceholder')}
                                id="filterRoles"
                                name="filterRoles"
                                data-testid="accounts-select-filterRoles"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MultiSelect
                                label={t('accounts.filterByUnit')}
                                value={filterUnits}
                                onChange={handleUnitFilterChange}
                                options={units}
                                placeholder={t('accounts.filterByUnitPlaceholder')}
                                id="filterUnits"
                                name="filterUnits"
                                data-testid="accounts-select-filterUnits"
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Table */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
                    <Table
                        id="accounts-table"
                        columns={columns}
                        data={accounts}
                        selectable={true}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        renderActions={renderTableActions}
                        loading={loading}
                        emptyMessage={t('accounts.noData')}
                        idField="id"
                        pagination={true}
                        page={page}
                        pageSize={pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[5, 10, 20, 50, 100]}
                        sortable={true}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        sortableColumns={['username', 'email', 'phone', 'role', 'unitName', 'positionName', 'status']}
                    />
                </Box>
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('accounts.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('accounts.deleteDialog.message', {
                            username: accountToDelete?.username || '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        id="cancelDelete"
                        name="cancelDelete"
                        data-testid="accounts-button-cancelDelete"
                    >
                        {t('accounts.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmDelete"
                        name="confirmDelete"
                        data-testid="accounts-button-confirmDelete"
                    >
                        {t('accounts.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('accounts.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('accounts.bulkDeleteDialog.message', {
                            count: selectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelBulkDelete}
                        variant="outlined"
                        id="cancelBulkDelete"
                        name="cancelBulkDelete"
                        data-testid="accounts-button-cancelBulkDelete"
                    >
                        {t('accounts.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmBulkDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDelete"
                        name="confirmBulkDelete"
                        data-testid="accounts-button-confirmBulkDelete"
                    >
                        {t('accounts.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Account Modal */}
            <AddAccountModal
                open={showAddDialog}
                onClose={handleCloseAddAccount}
                onConfirm={handleConfirmAddAccount}
                roles={roles}
                units={units}
                positions={positions}
                modules={modules}
                selectedRoleDetail={selectedRoleDetail}
                onFetchRoleById={(roleId) => dispatch(fetchRoleById(roleId))}
                onClearSelectedRoleDetail={() => dispatch(clearSelectedRoleDetail())}
            />

            {/* Edit Account Modal */}
            <AddAccountModal
                open={showEditDialog}
                onClose={handleCloseEditAccount}
                onConfirm={handleConfirmEditAccount}
                roles={roles}
                units={units}
                positions={positions}
                modules={modules}
                selectedRoleDetail={selectedRoleDetail}
                onFetchRoleById={(roleId) => dispatch(fetchRoleById(roleId))}
                onClearSelectedRoleDetail={() => dispatch(clearSelectedRoleDetail())}
                accountData={selectedAccount}
                editMode={true}
            />

            {/* OTP Login QR Modal */}
            <OtpQrModal
                open={showOtpModal}
                onClose={handleCloseOtpModal}
                otpData={otpLoginData}
                username={otpUserAccount?.username || ''}
                loading={otpLoginLoading}
                error={otpLoginError}
            />
        </Box>
    );
}

export default Accounts;
